import React, {useMemo} from 'react';
import {
  OVRecommendActionEnum,
  GlobalPermissionEnum,
} from '../../../../schema-types/globalTypes';
import {builderFieldsType} from '../builderFields';
import {useBuilderContext} from '../useBuilderContext';
import AutoField, {
  ActionType,
  RadioGroupField,
  SelectOption,
} from '../../../../components/AutoForm/AutoFieldNew';
import {useAutoForm} from '../../../../components/AutoForm/useAutoForm';
import {useDjangoContext} from '../../../../contexts/useAuthContext';
import {hasPermission} from '../../../../contexts/FeatureBoundary';

/**
 * Two separate labels are needed
 * One for the option input, the other for the header text
 */
type planActionsType = Partial<
  Record<OVRecommendActionEnum, {optionLabel: string; label: string; adviceLabel: string}>
>;

export const planActionsBasic: planActionsType = {
  [OVRecommendActionEnum.CONTRIBUTE]: {
    optionLabel: 'Contribute',
    label: OVRecommendActionEnum.CONTRIBUTE.toLowerCase(),
    adviceLabel: 'Contribute to',
  },
  [OVRecommendActionEnum.WITHDRAW]: {
    optionLabel: 'Withdraw',
    label: OVRecommendActionEnum.WITHDRAW.toLowerCase(),
    adviceLabel: 'Withdraw from',
  },
  [OVRecommendActionEnum.TRANSFER]: {
    optionLabel: 'Transfer',
    label: OVRecommendActionEnum.TRANSFER.toLowerCase(),
    adviceLabel: 'Transfer to',
  },
  [OVRecommendActionEnum.SET_UP_NEW]: {
    optionLabel: 'Set Up New',
    label: 'set up a new',
    adviceLabel: 'Set up a new',
  },
  [OVRecommendActionEnum.MODIFY]: {
    optionLabel: 'Modify',
    label: OVRecommendActionEnum.MODIFY.toLowerCase(),
    adviceLabel: 'Modify a',
  },
  [OVRecommendActionEnum.AD_HOC_FEE]: {
    optionLabel: 'Charge Ad-hoc Fee',
    label: 'Charge Ad-hoc Fee',
    adviceLabel: 'Charge an Ad-hoc Fee to',
  },
  [OVRecommendActionEnum.ACTION]: {
    adviceLabel: 'Action',
    label: OVRecommendActionEnum.ACTION.toLowerCase(),
    optionLabel: 'Action',
  },
};

export type PlanActionFlags = {
  isSam: boolean;
  isSPPDA: boolean;
  isSPPAR: boolean;
  canUseAdhocFees: boolean;
  sppType?: string | null;
};

export const getPlanActionOptions = (flags: PlanActionFlags): SelectOption[] =>
  Object.entries(planActionsBasic)
    .filter(([key]) => {
      if (key === OVRecommendActionEnum.SET_UP_NEW) {
        // DBO-1510: SAM users cannot set up new plans
        return !flags.isSam;
      }
      if (key === OVRecommendActionEnum.AD_HOC_FEE) {
        // NOTE: NOT INCLUDING FOR NOW AS STILL NOT RELEASED
        // show adhoc fees only for spp da and execution only/unselected sppType
        return Boolean(
          flags.canUseAdhocFees && flags.isSPPDA && flags.sppType === 'Advised'
        );
      }
      if (key === OVRecommendActionEnum.ACTION) {
        // action should only be for sfp and spp ar
        if (flags.isSPPDA) {
          return false;
        }
      }
      if (key === OVRecommendActionEnum.MODIFY) {
        // modify should only be for sfp
        if (flags.isSPPAR || flags.isSPPDA) {
          return false;
        }
      }
      return true;
    })
    .map<SelectOption>(([key, labels]) => ({
      label: labels.optionLabel,
      value: key,
    }));

export const BuilderWhatOptions: React.FC<{visible: boolean}> = (props) => {
  const {visible} = props;
  const {values, isSubmitting} = useAutoForm<builderFieldsType>();
  const {isSPPDA, isSPPAR} = useBuilderContext();
  const {djangoUser} = useDjangoContext();
  const canUseAdhocFees = hasPermission(djangoUser, [
    GlobalPermissionEnum.can_use_adhoc_fees,
  ]);

  const field: RadioGroupField = useMemo(() => {
    const label = values.planAction
      ? `to ${planActionsBasic[values.planAction]?.label}`
      : 'To ...';
    const options = getPlanActionOptions({
      isSam: Boolean(djangoUser?.isSam),
      isSPPDA,
      isSPPAR,
      canUseAdhocFees,
      sppType: values.sppType,
    });

    return {
      id: 'planAction',
      label,
      boldLabel: true,
      type: ActionType.radioGroup,
      size: 'small',
      options,
      disabled: isSubmitting,
    };
  }, [
    isSPPDA,
    isSPPAR,
    canUseAdhocFees,
    djangoUser?.isSam,
    values.sppType,
    values.planAction,
    isSubmitting,
  ]);

  if (!visible) {
    return null;
  }

  return <AutoField field={field} />;
};
